
import { useGetGlobalDiscoveryStore } from "@entities/global-discovery/store/useGetGlobalDiscoveryStoreV2";
import { ConfigGlobalTypeSingle } from "@shared/lib/types";
import { Button } from "@shared/ui";
import {
  LeftOutlined,
  RightOutlined,
} from "@shared/ui/icons";
import selectStyles from "@shared/ui/Select/styles.module.scss";
import { InputNumber, Popover } from "antd";
import { Check, ChevronDown } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import styles from "./RangeFilter.module.scss";

interface RangeFilterProps {
  filter: ConfigGlobalTypeSingle;
  showTitle?: boolean;
  selectedFilters?: any;
  setSelectedFilter?: (updateFn: (value: any) => any) => void;
}

const CUSTOM_OPTION_VALUE = "__custom__";

export function RangeFilter({
  filter,
  showTitle = true,
  selectedFilters: selectedFiltersProp,
  setSelectedFilter: setSelectedFilterProp,
}: RangeFilterProps) {
  const globalStore = useGetGlobalDiscoveryStore();
  const selectedFilters = selectedFiltersProp ?? globalStore.selectedFilters;
  const setSelectedFilter = setSelectedFilterProp ?? globalStore.setSelectedFilter;

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"list" | "custom">("list");
  const [customMin, setCustomMin] = useState<string>("");
  const [customMax, setCustomMax] = useState<string>("");
  const buttonRef = useRef<HTMLButtonElement>(null);

  const currentRange = useMemo(() => {
    const value = selectedFilters[filter.facetName];
    if (
      value &&
      typeof value === "object" &&
      ("min" in value || "max" in value)
    ) {
      return {
        min: typeof value.min === "number" ? value.min : undefined,
        max: typeof value.max === "number" ? value.max : undefined,
      };
    }
    return { min: undefined, max: undefined };
  }, [selectedFilters, filter.facetName]);

  // Check if optionsList exists
  const hasOptionsList = Boolean(
    filter.optionsList && filter.optionsList.length > 0
  );

  // Build options from optionsList + custom option
  const options = useMemo(() => {
    if (!hasOptionsList || !filter.optionsList) return [];

    const listOptions = filter.optionsList.map((item) => ({
      label: item.label,
      value:
        typeof item.value === "object" && item.value !== null
          ? JSON.stringify(item.value)
          : String(item.value),
      rangeValue: item.value,
    }));

    // Add custom option
    return [
      ...listOptions,
      {
        label: "Custom",
        value: CUSTOM_OPTION_VALUE,
        rangeValue: null,
      },
    ];
  }, [hasOptionsList, filter.optionsList]);

  // Get current selected range
  const currentData = useMemo(() => {
    const value = selectedFilters[filter.facetName];
    if (!value || typeof value !== "object") {
      return { selectedRange: undefined, isCustom: false };
    }

    // Check if it's a custom range (not matching any option)
    if ("min" in value || "max" in value) {
      const rangeValue = value as { min?: number; max?: number };

      // Check if this range matches any option in optionsList
      const matchingOption = filter.optionsList?.find((opt) => {
        if (typeof opt.value === "object" && opt.value !== null) {
          const optValue = opt.value as { min?: number; max?: number };
          return (
            optValue.min === rangeValue.min && optValue.max === rangeValue.max
          );
        }
        return false;
      });

      // If it matches an option, it's not custom
      const isCustom = !matchingOption;

      return {
        selectedRange: rangeValue,
        isCustom,
      };
    }

    return { selectedRange: undefined, isCustom: false };
  }, [selectedFilters, filter.facetName, filter.optionsList]);

  // Get current selected value for display
  const getDisplayLabel = () => {
    if (!currentData.selectedRange) {
      return filter.placeHolder || `Select ${filter.title}`;
    }

    if (currentData.isCustom) {
      const { min, max } = currentData.selectedRange;
      if (min !== undefined && max !== undefined) {
        return `${!showTitle ? filter.title + ": " : ""}${min}-${max}`;
      }
      if (min !== undefined) {
        return `${!showTitle ? filter.title + ": " : ""}${min}+`;
      }
      if (max !== undefined) {
        return `${!showTitle ? filter.title + ": " : ""}≤${max}`;
      }
      return `${!showTitle ? filter.title + ": " : ""}Custom`;
    }

    const matchingOption = filter.optionsList?.find((opt) => {
      if (typeof opt.value === "object" && opt.value !== null) {
        return (
          opt.value.min === currentData.selectedRange?.min &&
          opt.value.max === currentData.selectedRange?.max
        );
      }
      return false;
    });

    return `${!showTitle ? filter.title + ": " : ""}${
      matchingOption?.label || "Select range"
    }`;
  };

  const handleOptionClick = (opt: {
    label: string;
    value: string;
    rangeValue: any;
  }) => {
    if (opt.value === CUSTOM_OPTION_VALUE) {
      // Switch to custom view
      setView("custom");
      // Initialize custom inputs with current range if exists
      if (currentData.selectedRange) {
        setCustomMin(
          currentData.selectedRange.min !== undefined
            ? String(currentData.selectedRange.min)
            : ""
        );
        setCustomMax(
          currentData.selectedRange.max !== undefined
            ? String(currentData.selectedRange.max)
            : ""
        );
      }
      return;
    }

    // Handle fixed option selection
    let rangeValue: { min?: number; max?: number } | undefined;
    if (typeof opt.rangeValue === "object" && opt.rangeValue !== null) {
      rangeValue = opt.rangeValue as { min?: number; max?: number };
    } else {
      try {
        rangeValue = JSON.parse(opt.value) as { min?: number; max?: number };
      } catch {
        // Invalid value
        return;
      }
    }

    setSelectedFilter((prevFilters) => {
      return {
        ...prevFilters,
        [filter.facetName]: rangeValue,
        filterTags: {
          ...(prevFilters.filterTags || {}),
          [filter.facetName]: opt.label,
        },
      };
    });

    setOpen(false);
    setView("list");
  };

  const handleApplyCustom = () => {
    const minValue =
      customMin && !isNaN(Number(customMin)) ? Number(customMin) : undefined;
    const maxValue =
      customMax && !isNaN(Number(customMax)) ? Number(customMax) : undefined;

    // Only set if at least one value exists
    if (minValue === undefined && maxValue === undefined) {
      return;
    }

    const customRange = {
      min: minValue,
      max: maxValue,
    };

    // Create label for custom range
    let label = "Custom";
    if (minValue !== undefined && maxValue !== undefined) {
      label = `${minValue}-${maxValue}`;
    } else if (minValue !== undefined) {
      label = `${minValue}+`;
    } else if (maxValue !== undefined) {
      label = `≤${maxValue}`;
    }

    setSelectedFilter((prevFilters) => {
      return {
        ...prevFilters,
        [filter.facetName]: customRange,
        filterTags: {
          ...(prevFilters.filterTags || {}),
          [filter.facetName]: label,
        },
      };
    });

    setOpen(false);
    setView("list");
    setCustomMin("");
    setCustomMax("");
  };

  const handleClear = () => {
    setSelectedFilter((prevFilters) => {
      return {
        ...prevFilters,
        [filter.facetName]: undefined,
        filterTags: {
          ...(prevFilters.filterTags || {}),
          [filter.facetName]: undefined,
        },
      };
    });
    setOpen(false);
    setView("list");
  };

  const hasActiveValue = currentData.selectedRange !== undefined;

  // Get chevron className based on state
  const getChevronClassName = () => {
    if (hasActiveValue) {
      return selectStyles.chevronActive;
    }
    return selectStyles.chevronDefault;
  };

  // Get current selected option value for highlighting
  const getSelectedValue = () => {
    if (!currentData.selectedRange) return undefined;

    if (currentData.isCustom) {
      return CUSTOM_OPTION_VALUE;
    }

    const matchingOption = filter.optionsList?.find((opt) => {
      if (typeof opt.value === "object" && opt.value !== null) {
        return (
          opt.value.min === currentData.selectedRange?.min &&
          opt.value.max === currentData.selectedRange?.max
        );
      }
      return false;
    });

    if (matchingOption) {
      return JSON.stringify(matchingOption.value);
    }

    return CUSTOM_OPTION_VALUE;
  };

  if (!hasOptionsList) {
    // Fallback to original behavior if no optionsList
    const handleMinChange = (value: number | string | null) => {
      const minValue =
        value !== null && typeof value === "number" ? value : undefined;

      setSelectedFilter((prevFilters) => {
        const newRange = {
          min: minValue,
          max: currentRange.max,
        };

        const hasValue =
          minValue !== undefined || currentRange.max !== undefined;

        return {
          ...prevFilters,
          [filter.facetName]: hasValue ? newRange : undefined,
          filterTags: {
            ...(prevFilters.filterTags || {}),
            [`${filter.facetName}_min`]: minValue,
            [`${filter.facetName}_max`]: currentRange.max,
          },
        };
      });
    };

    const handleMaxChange = (value: number | string | null) => {
      const maxValue =
        value !== null && typeof value === "number" ? value : undefined;

      setSelectedFilter((prevFilters) => {
        const newRange = {
          min: currentRange.min,
          max: maxValue,
        };

        const hasValue =
          currentRange.min !== undefined || maxValue !== undefined;

        return {
          ...prevFilters,
          [filter.facetName]: hasValue ? newRange : undefined,
          filterTags: {
            ...(prevFilters.filterTags || {}),
            [`${filter.facetName}_min`]: currentRange.min,
            [`${filter.facetName}_max`]: maxValue,
          },
        };
      });
    };

    return (
      <div className={styles.rangeFilter}>
        {showTitle && <label className={styles.label}>{filter.title}</label>}
        <div className={styles.inputsContainer}>
          <InputNumber
            size="small"
            className={styles.input}
            placeholder="Min"
            value={currentRange.min ?? null}
            onChange={handleMinChange}
          />
          <InputNumber
            size="small"
            className={styles.input}
            placeholder="Max"
            value={currentRange.max ?? null}
            onChange={handleMaxChange}
          />
        </div>
      </div>
    );
  }

  const selectedValue = getSelectedValue();

  const renderDropdownContent = () => (
    <div
      className={selectStyles.searchableDropdown}
      style={{ width: buttonRef.current?.offsetWidth || "auto" }}
    >
      <div className={selectStyles.dropdownPanel}>
        {view === "list" ? (
          <div className={selectStyles.dropdownBody}>
            <div className={selectStyles.optionsList}>
              {options.map((opt) => {
                const selected = selectedValue === opt.value;
                const isCustom = opt.value === CUSTOM_OPTION_VALUE;
                return (
                  <div
                    key={opt.value}
                    onClick={() => handleOptionClick(opt)}
                    className={`${selectStyles.optionItem} ${
                      selected ? selectStyles.optionItemSelected : ""
                    }`}
                  >
                    <span className={selectStyles.optionLabel}>
                      {opt.label}
                    </span>
                    {selected && (
                      <Check size={14} className={selectStyles.checkIcon} />
                    )}
                    {isCustom && !selected && (
                      <RightOutlined size={14} className={styles.chevronIcon} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className={selectStyles.dropdownBody}>
            <div className={styles.customView}>
              <div className={styles.customHeader}>
                <button
                  type="button"
                  onClick={() => setView("list")}
                  className={styles.backButton}
                >
                  <LeftOutlined size={16} />
                </button>
                <span className={styles.customTitle}>Custom Range</span>
              </div>
              <div className={styles.customInputs}>
                <div className={styles.inputGroup}>
                  <label className={styles.inputLabel}>From</label>
                  <input
                    type="number"
                    value={customMin}
                    onChange={(e) => setCustomMin(e.target.value)}
                    className={styles.customInput}
                    placeholder="Min value"
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label className={styles.inputLabel}>To</label>
                  <input
                    type="number"
                    value={customMax}
                    onChange={(e) => setCustomMax(e.target.value)}
                    className={styles.customInput}
                    placeholder="Max value"
                  />
                </div>
                <Button
                  type="primary"
                  onClick={handleApplyCustom}
                  className={styles.applyButton}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.rangeFilter}>
      {showTitle && <label className={styles.label}>{filter.title}</label>}
      <div className={styles.selectContainer}>
        <Popover
          content={renderDropdownContent()}
          trigger="click"
          open={open}
          onOpenChange={setOpen}
          placement="bottomLeft"
          overlayClassName={styles.popoverOverlay}
          getPopupContainer={() => document.body}
          destroyTooltipOnHide={false}
          autoAdjustOverflow={false}
          zIndex={1050}
        >
          <button
            ref={buttonRef}
            type="button"
            className={`${selectStyles.selectButton} ${
              selectStyles.selectButtonMiddle
            } ${
              hasActiveValue
                ? styles.selectButtonActive
                : styles.selectButtonDefault
            }`}
          >
            <span className={selectStyles.buttonText}>{getDisplayLabel()}</span>
            <ChevronDown
              size={14}
              className={`${selectStyles.chevron} ${getChevronClassName()} ${
                open ? selectStyles.chevronOpen : ""
              }`}
            />
          </button>
        </Popover>
      </div>
    </div>
  );
}
